import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import { useAuth } from 'contexts/auth';
import { useCallback, useEffect, useState } from 'react';
import { getLocaleMap } from 'utils/locales';
import { useNavigate } from 'react-router-dom';
import { handleErrorResponse } from 'utils/general';
import { Divider } from '@mui/material';
import moment from 'moment';
import DataTable from 'components/DataTable';
import { withLoading } from 'utils/hoc';
import Image from 'components/Image';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { getMonitorItems } from 'api/monitors';

const MonitorItemTableSection = ({ title_color, title_font_size, ...props }) => {
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const [rowsData, setRowsData] = useState([]);

  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap([])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const handleClickMonitorItem = useCallback((id) => {
    navigate(`/monitor_details${id && `?monitor_item_id=${id}`}`);
  }, [navigate]);

  const columns = [
    {
      field: 'monitor_item_id',
      sortable: false,
      renderContent: (data) => {
        const { model, expected_lowest_price, expected_highest_price, condition, currency, monitor_type, createddate } = data;
        const expectedLowestPirce = expected_lowest_price ? expected_lowest_price.toLocaleString() : 'Any Price';
        const expectedHighestPirce = expected_highest_price ? expected_highest_price.toLocaleString() : 'Any Price';
        return (
          <MKBox mb={1} padding="0.5rem" maxWidth="100%" style={{ cursor: 'pointer' }}>
            <MKBox width="100%" padding={1} style={{ border: '1px solid lightgray' }} display="flex" flexDirection="column" onClick={() => handleClickMonitorItem(data?.monitor_item_id)}>
              <MKBox display="flex" justifyContent="space-between">
                <MKTypography mb={1} fontSize="16px" color={title_color}>
                  {model?.id_name}
                </MKTypography>
                <MKTypography mb={1} fontSize="16px" color="gray">
                  {model?.brand_name}
                </MKTypography>
              </MKBox>
              <Divider sx={{ bgcolor: 'gray', margin: 0, marginBottom: '1rem' }} />
              <MKBox display="flex">
                <MKBox display="flex" flex={1} mr="1rem">
                  <Image src={model?.default_image?.file_url} width="100%" />
                </MKBox>
                <MKBox display="flex" flex={3} flexDirection="column" justifyContent="space-between">
                  <MKTypography fontSize="16px" color={monitor_type === 'sell' ? 'blue' : 'red'}>
                    {monitor_type === 'sell' ? 'Selling' : 'Looking'}
                  </MKTypography>
                  <MKTypography fontSize="16px" color={title_color}>
                    {(expected_highest_price || expected_lowest_price) ? `${currency || ''} ${expectedLowestPirce} - ${expectedHighestPirce}` : `${currency || ''} Any Price`}
                  </MKTypography>
                  <MKBox display="flex" justifyContent="space-between" mt="2rem">
                    <MKTypography fontSize="12px" color={title_color} padding="0.1rem 0.5rem" sx={{ backgroundColor: '#eaeffa', marginRight: '0.5rem' }}>
                      {condition.charAt(0).toUpperCase() + condition.slice(1)}
                    </MKTypography>
                    <MKTypography fontSize="14px" color="gray" textAlign="right">
                      {`Monitored for ${moment(createddate).fromNow()}`}
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </MKBox>
            </MKBox>
          </MKBox>
        );
      },
    },
  ];

  const fetchMonitorItemsFromApi = useCallback(() => {
    const params = {
      $expand: 'model/default_image',
      user: auth?.user?.username,
    }
    return getMonitorItems(params)
      .then(({ data }) => {
        setRowsData(data);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [auth?.user?.username, setAuth]);

  useEffect(() => {
    fetchMonitorItemsFromApi();
  }, [fetchMonitorItemsFromApi]);

  return (
    <MKBox
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <MKBox display="flex" alignItems="center" width="100%" marginBottom="1rem" padding="0.5rem 1rem">
        <MKBox display="flex" flex={1}>
          <ArrowBackIosNewIcon fontSize="medium" style={{ cursor: 'pointer', color: title_color }} onClick={() => navigate('/monitors')} />
        </MKBox>
        <MKBox display="flex" flex={2}>
          <MKTypography width="100%" color={title_color} fontSize={title_font_size} textAlign="center">
            Config Monitors
          </MKTypography>
        </MKBox>
        <MKBox display="flex" justifyContent="flex-end" flex={1}>
          <AddIcon fontSize="large" style={{ cursor: 'pointer', color: title_color }} onClick={() => handleClickMonitorItem('')} />
        </MKBox>
      </MKBox>
      <MKBox display="flex" flexDirection="column" width="100%">
        <DataTable
          data={rowsData}
          columns={columns}
          showHeader={false}
          paginationDisabled
        />
      </MKBox>
    </MKBox>
  );
};

MonitorItemTableSection.propTypes = {
  table_name: PropTypes.string,
  header_color: PropTypes.string,
  title_color: PropTypes.string,
  title_font_size: PropTypes.string,
  page_options: PropTypes.string,
  // setLoading: PropTypes.func,
  show_header: PropTypes.bool,
  extra_params: PropTypes.object,
  refresh_second: PropTypes.number,
  brand_options: PropTypes.array,
  model_options: PropTypes.array,
  source_options: PropTypes.array,
  currency_options: PropTypes.array,
};

MonitorItemTableSection.defaultProps = {
  table_name: '',
  header_color: '#2c589d',
  title_color: '#000',
  title_font_size: '',
  page_options: [],
  // setLoading: null,
  show_header: false,
  extra_params: {},
  refresh_second: 10,
  brand_options: [],
  model_options: [],
  source_options: [],
  currency_options: [],
};

export default MonitorItemTableSection;
